import { defineStore } from 'pinia'
import type { Lead, LeadStatus } from '@/types/leads'
import LeadsService from '@/services/leads.service' 
import { useContactsStore } from '@/stores/contact.store'
import { useSettingsStore } from '@/stores/settings.store'
import { toast } from 'vue3-toastify'

interface LeadForm {
  name: string;
  company: string | null;
  estimated_value: number;
  source: string;
  interest: string;
  notes: string | null;
  status: string;
  contact_id: number | null;
}

interface PaginationState {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
}

type ServerCounts = Record<string, number>

interface LeadsState {
  leads: Lead[];
  loading: boolean;
  activeTab: string;
  isModalOpen: boolean;
  pagination: PaginationState;
  listParams: {
    page: number;
    per_page: number;
    filter: {
      search: string;
      status: string;
    };
  };
  form: LeadForm;
  serverCounts: ServerCounts;
}

export const useLeadsStore = defineStore('leads', {
  state: (): LeadsState => ({
    leads: [],
    loading: false,
    activeTab: 'All',
    isModalOpen: false,
    pagination: {
      current_page: 1,
      last_page: 1,
      per_page: 10,
      total: 0
    },
    listParams: {
      page: 1,
      per_page: 10,
      filter: { search: '', status: '' }
    },
    form: {
      name: '',
      company: null,
      estimated_value: 0,
      source: '',
      interest: '',
      notes: null,
      status: '',
      contact_id: null
    },
    serverCounts: { all: 0, new: 0, contacted: 0, qualified: 0, converted: 0, lost: 0 }
  }),

  getters: {
    allLeads: (state) => state.leads,
    counts: (state) => state.serverCounts
  },

  actions: {
    async index(options: { background: boolean } = { background: false }): Promise<void> {
      if (this.loading && !options.background) return

      if (!options.background) {
        this.loading = true
      }
      
      try {
        const response = await LeadsService.index(this.listParams)
        const result = response.data?.data
        
        if (result) {
          const paginatedData = result.paginated
          this.leads = (paginatedData?.data || []) as Lead[]
          
          this.pagination = {
            current_page: paginatedData?.current_page || 1,
            last_page: paginatedData?.last_page || 1,
            per_page: paginatedData?.per_page || 10,
            total: paginatedData?.total || 0
          }

          this.listParams.page = this.pagination.current_page
          this.listParams.per_page = this.pagination.per_page

          if (result.counts) {
            this.serverCounts = result.counts
          }
        }
      } catch (error) {
        console.error('Failed to load paginated leads collection:', error)
      } finally {
        if (!options.background) {
          this.loading = false
        }
      }
    },

    async changeTab(tabName: string): Promise<void> {
      this.activeTab = tabName
      this.listParams.filter.status = tabName.toLowerCase() === 'all' ? '' : tabName
      this.listParams.page = 1 
      await this.index()
    },

    async setPage(page: number): Promise<void> {
      if (page < 1 || page > this.pagination.last_page || page === this.listParams.page) return
      this.listParams.page = page
      await this.index()
    },

    async setPerPage(perPage: number): Promise<void> {
      if (this.listParams.per_page === perPage) return

      this.listParams.per_page = perPage
      this.listParams.page = 1
      await this.index()
    },

    async nextPage(): Promise<void> {
      if (this.listParams.page >= this.pagination.last_page) return
      this.listParams.page++
      await this.index()
    },

    async prevPage(): Promise<void> {
      if (this.listParams.page <= 1) return
      this.listParams.page--
      await this.index()
    },

    async updateStatus(id: number, newStatus: LeadStatus | string): Promise<void> {
      try {
        await LeadsService.updateStatus(id, newStatus)

        await this.index({ background: true })
      } catch (error) {
        console.error('Failed toggling status patch:', error)
      }
    },

    async store(): Promise<void> {
      const contacts_store = useContactsStore()
      const contactForm = contacts_store.form
      this.loading = true
      try {
        await LeadsService.store({
          ...this.form,
          ...(!this.form.contact_id && {
            contact: {
              full_name: this.form.name,
              email: contactForm.email,
              phone: contactForm.phone,
            }
          })
        })
        toast.success('Lead created successfully.')
        this.resetForm()
        contacts_store.resetForm()
        this.isModalOpen = false
        this.listParams.page = 1
        await this.index()
      } catch (error) {
        console.error(error)
        throw error
      } finally {
        this.loading = false
      }
    },

    async storeFromContact(payload: {
      contact_id: number
      interest: string
      estimated_value: number
      company: string | null
      status: string
      source: string
    }): Promise<void> {
      try {
        await LeadsService.storeFromContact(payload)
      } catch (error) {
        console.error(error)
        throw error
      }
    },

    async update(id: number, data: Record<string, any>): Promise<void> {
      const contacts_store = useContactsStore()
      try {
        await LeadsService.update(id, data)
        toast.success('Lead updated successfully.')
        this.resetForm()
        contacts_store.resetForm()
        this.isModalOpen = false
        await this.index()
      } catch (error) {
        console.error(error)
        throw error
      }
    },

    async destroy(id: number): Promise<void> {
      try {
        await LeadsService.destroy(id)
        
        if (this.leads.length === 1 && this.listParams.page > 1) {
          this.listParams.page--
        }
        
        await this.index({ background: true })
      } catch (error) {
        console.error(error)
        throw error
      }
    },
    
    resetForm(): void {
      const settingsStore = useSettingsStore()
      const defaultStatus = settingsStore.leadStages[0] ?? 'New'

      this.form = { 
        name: '', 
        company: null, 
        estimated_value: 0, 
        source: '', 
        interest: '', 
        notes: null,
        status: defaultStatus, 
        contact_id: null
        
      }
    }
  }
})
